import { ref } from 'vue'
import apiClient from '@/api/client'

/** Site-wide SEO state loaded once from /api/settings/public */
const siteName = ref('Casino')
const siteDescription = ref('')
const siteOgImage = ref('')
let loaded = false

/**
 * Loads site SEO settings from the public API.
 * Called once during app bootstrap — results are cached in module-level refs.
 */
export async function loadSeoSettings(): Promise<void> {
  if (loaded) return
  try {
    const response = await apiClient.get<{
      site_name?: string
      site_description?: string
      site_og_image?: string
    }>('/api/settings/public')
    const data = response.data
    if (data?.site_name) siteName.value = data.site_name
    if (data?.site_description) siteDescription.value = data.site_description
    if (data?.site_og_image) siteOgImage.value = data.site_og_image
    loaded = true
  } catch {
    // API unavailable — keep defaults
  }
}

function setMetaTag(attr: string, key: string, content: string): void {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!content) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/**
 * Updates document title and meta tags for the current page.
 *
 * @param pageTitle — The page-specific title (e.g. "Game Lobby", "Blackjack")
 * @param options.description — Page-specific meta description
 * @param options.ogImage — Page-specific OG image URL
 */
export function updateSeo(
  pageTitle?: string,
  options?: { description?: string; ogImage?: string },
): void {
  // Title: "Page Title — Site Name" or just "Site Name"
  document.title = pageTitle
    ? `${pageTitle} — ${siteName.value}`
    : siteName.value

  const desc = options?.description || siteDescription.value
  const ogImg = options?.ogImage || siteOgImage.value

  // Standard meta
  setMetaTag('name', 'description', desc)

  // Open Graph
  setMetaTag('property', 'og:title', document.title)
  setMetaTag('property', 'og:description', desc)
  setMetaTag('property', 'og:image', ogImg)

  // Twitter Card
  setMetaTag('name', 'twitter:title', document.title)
  setMetaTag('name', 'twitter:description', desc)
  setMetaTag('name', 'twitter:image', ogImg)
}

export function useSeo() {
  return {
    siteName,
    siteDescription,
    siteOgImage,
    updateSeo,
    loadSeoSettings,
  }
}
